import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,  // THIS IS IMPORTANT
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    // console.log('🔐 [API] Making request with token:', token ? 'Yes' : 'No');
    // console.log('🔐 [API] Request URL:', config.url);
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // console.log('🔐 [API] Authorization header set');
    } else {
      console.warn('⚠️ [API] No authentication token found');
    }
    
    return config;
  },
  (error) => {
    console.error('❌ [API] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Dashboard API
export const dashboardAPI = {
  getStats: () => 
    api.get('/admin/dashboard'),
  
  getRecentActivity: () => 
    api.get('/admin/dashboard/recent-activity'),

  // ADD THIS METHOD - Consolidated dashboard stats endpoint
  getDashboardStats: () => 
    api.get('/admin/analytics/dashboard-stats'),
};

// Analytics API
export const analyticsAPI = {
  getTraffic: (params) => 
    api.get('/admin/analytics/traffic', { params }),
  
  getSummary: () => 
    api.get('/admin/analytics/summary'),

  // ADD THIS METHOD - Consolidated dashboard stats endpoint
  getDashboardStats: () => 
    api.get('/admin/analytics/dashboard-stats'),
};

// Bookkeepers API - FIXED PATHS
export const bookkeepersAPI = {
  // Get all bookkeepers with filters
  getAll: (params) => 
    api.get('/admin/bookkeepers', { params }),

  getFilterOptions: () => 
  api.get('/admin/bookkeepers/filter-options'),
  
  getBookkeepers: (params) => 
    api.get('/admin/bookkeepers', { params }),
  
  // Get single bookkeeper by ID - FIXED PATH
  getById: (id) => 
  api.get(`/admin/bookkeepers/detail/${id}`),
  
  // Get single bookkeeper detail - FIXED PATH
  getDetail: (id) =>
    api.get(`/admin/bookkeepers/detail/${id}`),
  
  // Check if bookkeeper exists - FIXED PATH
  checkExists: (id) =>
    api.get(`/admin/bookkeepers/check/${id}`),
  
  // Get bookkeepers by zipcode
  getByZipcode: (zipCode, params) => 
    api.get(`/admin/bookkeepers/zipcode/${zipCode}`, { params }),
  
  // Get pending bookkeepers
  getPending: (params) => 
    api.get('/admin/bookkeepers/pending', { params }),
  
  // Get expired bookkeepers
  getExpired: (params) => 
    api.get('/admin/bookkeepers/expired', { params }),
  
  // Get expiring soon bookkeepers
  getExpiringSoon: () => 
    api.get('/admin/bookkeepers/expiring-soon'),
  
  // Approve bookkeeper
  approve: (id, data) => 
    api.post(`/admin/bookkeepers/${id}/approve`, data),
  
  // Reject bookkeeper
  reject: (id, data) => 
    api.post(`/admin/bookkeepers/${id}/reject`, data),
  
  // Extend expiration
  extend: (id, data) => 
    api.post(`/admin/bookkeepers/${id}/extend`, data),
  
  // Extend expiration with duration
  extendDuration: (id, data) => 
    api.post(`/admin/bookkeepers/${id}/extend-duration`, data),
  
  // Renew bookkeeper
  renew: (id, data) => 
    api.post(`/admin/bookkeepers/${id}/renew`, data),
  
  // Bulk extend expiration
  bulkExtend: (data) => 
    api.post('/admin/bookkeepers/bulk-extend', data),
  
  // Update bookkeeper
  update: (id, data) => 
    api.put(`/admin/bookkeepers/${id}`, data),
  
  // Delete bookkeeper
  delete: (id) => 
    api.delete(`/admin/bookkeepers/${id}`),

  // Add create method
  create: (data) => 
    api.post('/admin/bookkeepers', data),
};

// Admin Auth API
export const authAPI = {
  login: (credentials) => 
    api.post('/admin/auth/login', credentials),
  
  logout: () => 
    api.post('/admin/auth/logout'),
  
  getProfile: () => 
    api.get('/admin/auth/profile'),
};

// Zipcodes API - ENHANCED
export const zipcodesAPI = {
  getZipcodes: (params) => 
    api.get('/admin/zipcodes', { params }),
  
  // Get bookkeepers by specific zipcode
  getBookkeepersByZipcode: (zipCode, params) => 
    api.get(`/admin/zipcodes/${zipCode}/bookkeepers`, { params }),
  
  // Get geographic analytics
  getGeographicAnalytics: () => 
    api.get('/admin/zipcodes/analytics/geographic'),
  
  createZipcode: (data) => 
    api.post('/admin/zipcodes', data),
  
  updateZipcode: (id, data) => 
    api.put(`/admin/zipcodes/${id}`, data),
  
  deleteZipcode: (id) => 
    api.delete(`/admin/zipcodes/${id}`),
};

// Users API
export const usersAPI = {
  getUsers: (params = {}) => 
    api.get('/admin/users', { params }),

  createUser: (userData) => 
    api.post('/admin/users', userData),

  updateUser: (id, userData) => 
    api.put(`/admin/users/${id}`, userData),

  deleteUser: (id) => 
    api.delete(`/admin/users/${id}`),
};

// Admin Users API
export const adminUsersAPI = {
  getAdmins: (params) => 
    api.get('/admin/users', { params }),
  
  createAdmin: (data) => 
    api.post('/admin/users', data),
  
  updateAdmin: (id, data) => 
    api.put(`/admin/users/${id}`, data),
  
  deleteAdmin: (id) => 
    api.delete(`/admin/users/${id}`),
};

// Expiration API - FIXED PATHS
export const expirationAPI = {
  // Get expiration statistics
  getStats: () => 
    api.get('/admin/bookkeepers/expiration/stats'),
  
  // Get expired bookkeepers
  getExpired: (params) => 
    api.get('/admin/bookkeepers/expiration/expired', { params }),
  
  // Get upcoming expired bookkeepers
  getUpcoming: (params) => 
    api.get('/admin/bookkeepers/expiration/upcoming', { params }),
  
  // Get expiring soon bookkeepers
  getExpiringSoon: () => 
    api.get('/admin/bookkeepers/expiring-soon'),
  
  // Extend expiration for individual bookkeeper
  extend: (id, data) => 
    api.post(`/admin/bookkeepers/${id}/extend-duration`, data),
  
  // Extend bookkeeper expiration (alias)
  extendBookkeeper: (id, durationMonths) => 
    api.post(`/admin/bookkeepers/${id}/extend-duration`, { durationMonths }),
  
  // Bulk extend expiration
  bulkExtend: (data) => 
    api.post('/admin/bookkeepers/bulk-extend', data),
  
  // Bulk extend expiration (alias)
  bulkExtendExpiration: (bookkeeperIds, durationMonths) => 
    api.post('/admin/bookkeepers/bulk-extend', { bookkeeperIds, durationMonths }),
  
  // Renew expired bookkeeper
  renew: (id, data) => 
    api.post(`/admin/bookkeepers/${id}/renew`, data),
  
  // Renew bookkeeper (alias)
  renewBookkeeper: (id, durationMonths) => 
    api.post(`/admin/bookkeepers/${id}/renew`, { durationMonths }),
};

// Combined admin API object for easier imports
export const adminAPI = {
  ...authAPI,
  ...dashboardAPI,
  ...bookkeepersAPI,
  ...expirationAPI,
  ...analyticsAPI,
  ...zipcodesAPI,
  ...adminUsersAPI,
  ...usersAPI
};

// Test function to verify exports
// console.log('✅ [API] adminAPI methods:', Object.keys(adminAPI));
// console.log('✅ [API] adminAPI.getDashboardStats exists:', typeof adminAPI.getDashboardStats);

export default api;